import {
  AspectOverrides, AspectRatio, ClipFraming, TextFont, Timeline,
} from '../../types/editor';

/** Canvas font stacks per overlay font key. Custom families come from
 * styles/fonts.css; files match backend/editor/fonts so preview and
 * render agree. Keep keys in sync with TEXT_FONTS in
 * backend/editor/render.py. */
export const TEXT_FONT_FAMILIES: Record<TextFont, string> = {
  sans:    "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
  impact:  "'OverlayImpact', Impact, sans-serif",
  bangers: "'OverlayBangers', Impact, sans-serif",
  marker:  "'OverlayMarker', 'Comic Sans MS', cursive",
  lobster: "'OverlayLobster', cursive",
};

export const TEXT_FONT_LABELS: Record<TextFont, string> = {
  sans: 'Sans (default)',
  impact: 'Impact',
  bangers: 'Bangers',
  marker: 'Marker',
  lobster: 'Lobster',
};

/**
 * Timeline schema migration — frontend mirror of
 * `backend/editor/timeline_schema.py`. When the backend bumps
 * `TIMELINE_VERSION`, add the matching upgrade step here and chain it
 * in `migrateTimeline`.
 *
 * v2: adds per-aspect overrides under `aspects.<aspect>.clipFraming`.
 * v3 (current): adds the text overlay track under `overlay.clips`.
 */

export const TIMELINE_VERSION = 3;

export const ALL_ASPECTS: AspectRatio[] = ['9:16', '1:1', '16:9', '4:5'];

const ASPECT_DIMENSIONS: Record<AspectRatio, { width: number; height: number }> = {
  '9:16': { width: 1080, height: 1920 },
  '1:1':  { width: 1080, height: 1080 },
  '16:9': { width: 1920, height: 1080 },
  '4:5':  { width: 1080, height: 1350 },
};

export const DEFAULT_FRAMING: ClipFraming = { scale: 1.0, offsetX: 0.0, offsetY: 0.0 };

export function dimensionsForAspect(aspect: AspectRatio): { width: number; height: number } {
  return ASPECT_DIMENSIONS[aspect] || ASPECT_DIMENSIONS['9:16'];
}

function emptyAspectMap(): Record<AspectRatio, AspectOverrides> {
  const out = {} as Record<AspectRatio, AspectOverrides>;
  for (const a of ALL_ASPECTS) out[a] = { clipFraming: {} };
  return out;
}

export function defaultTimeline(aspectPreset?: AspectRatio): Timeline {
  const aspect = aspectPreset || '9:16';
  const { width, height } = dimensionsForAspect(aspect);
  return {
    version: TIMELINE_VERSION,
    project: { aspect, width, height, duration: 0, fps: 30, bgColor: '#000000' },
    video: { clips: [] },
    audio: { clips: [] },
    overlay: { clips: [] },
    aspects: emptyAspectMap(),
  };
}

export function migrateTimeline(raw: unknown): Timeline {
  if (!raw || typeof raw !== 'object') return defaultTimeline();

  // Defensive deep-copy so we don't mutate the caller's object.
  const t = JSON.parse(JSON.stringify(raw)) as Partial<Timeline> & Record<string, unknown>;
  if (typeof t.version !== 'number') t.version = 0;

  // v1 → v2: gain an empty per-aspect overrides map. v1 projects
  // continue to render with default fit-inside framing until the user
  // explicitly customizes per aspect.
  if ((t.version as number) < 2) {
    (t as Partial<Timeline>).aspects = emptyAspectMap();
    t.version = 2;
  }

  // v2 → v3: gain the (empty) text overlay track.
  if ((t.version as number) < 3) {
    (t as Partial<Timeline>).overlay = { clips: [] };
    t.version = 3;
  }

  const project = (t.project as Partial<Timeline['project']> | undefined) || {};
  if (!project.aspect)   project.aspect = '9:16';
  if (!project.width)    project.width = 1080;
  if (!project.height)   project.height = 1920;
  if (typeof project.duration !== 'number') project.duration = 0;
  if (!project.fps)      project.fps = 30;
  if (!project.bgColor)  project.bgColor = '#000000';

  const video = (t.video as { clips?: unknown } | undefined) || { clips: [] };
  if (!Array.isArray(video.clips)) video.clips = [];

  const audio = (t.audio as { clips?: unknown } | undefined) || { clips: [] };
  if (!Array.isArray(audio.clips)) audio.clips = [];

  const overlay = (t.overlay as { clips?: unknown } | undefined) || { clips: [] };
  if (!Array.isArray(overlay.clips)) overlay.clips = [];

  // `aspects` might be missing keys or have a corrupted shape. Fill in
  // the missing slots so callers can index without guards.
  const aspectsRaw = (t.aspects as Partial<Record<AspectRatio, AspectOverrides>> | undefined) || {};
  const aspects = emptyAspectMap();
  for (const a of ALL_ASPECTS) {
    const incoming = aspectsRaw[a];
    if (incoming && typeof incoming === 'object' && incoming.clipFraming) {
      aspects[a] = { clipFraming: { ...incoming.clipFraming } };
    }
  }

  return {
    version: TIMELINE_VERSION,
    project: project as Timeline['project'],
    video: video as Timeline['video'],
    audio: audio as Timeline['audio'],
    overlay: overlay as Timeline['overlay'],
    aspects,
  };
}

/** Return the framing for a given (aspect, clip), clamped to safe
 * ranges and falling back to identity. Mirrors backend
 * `framing_for`. */
export function framingFor(
  timeline: Timeline, aspect: AspectRatio, clipId: string,
): ClipFraming {
  const slot = timeline.aspects?.[aspect]?.clipFraming?.[clipId];
  if (!slot) return DEFAULT_FRAMING;
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  return {
    // Up to 8× so a mismatched-aspect clip can zoom well past "cover"
    // (the crop window trims the overflow). Keep in sync with the
    // backend clamp in editor/timeline_schema.py framing_for().
    scale: clamp(Number(slot.scale ?? 1), 0.25, 8),
    offsetX: clamp(Number(slot.offsetX ?? 0), -1, 1),
    offsetY: clamp(Number(slot.offsetY ?? 0), -1, 1),
  };
}

/** Zoom (relative to the contain fit) that makes an asset FILL a given
 * aspect's canvas — "cover": shortest side expands until the frame is
 * full, overflow cropped. 1 when source dimensions are unknown. */
export function coverScaleFor(
  assetW: number | null | undefined,
  assetH: number | null | undefined,
  aspect: AspectRatio,
): number {
  if (!assetW || !assetH) return 1;
  const dims = dimensionsForAspect(aspect);
  const contain = Math.min(dims.width / assetW, dims.height / assetH);
  const cover = Math.max(dims.width / assetW, dims.height / assetH);
  if (!Number.isFinite(contain) || contain <= 0) return 1;
  return Math.min(8, Math.round((cover / contain) * 1000) / 1000);
}

/** Seed cover framing for a new clip across every aspect whose canvas
 * the asset doesn't already fill. Returns a new aspects map; entries
 * are real overrides, so the user can still zoom/pan or Reset from
 * there. */
export function withCoverFraming(
  aspects: Timeline['aspects'],
  clipId: string,
  assetW: number | null | undefined,
  assetH: number | null | undefined,
): Timeline['aspects'] {
  let next = aspects;
  for (const a of ALL_ASPECTS) {
    const scale = coverScaleFor(assetW, assetH, a);
    if (scale <= 1.001) continue;
    next = {
      ...next,
      [a]: {
        clipFraming: {
          ...next[a]?.clipFraming,
          [clipId]: { scale, offsetX: 0, offsetY: 0 },
        },
      },
    };
  }
  return next;
}

export function isDefaultFraming(f: ClipFraming): boolean {
  return Math.abs(f.scale - 1) < 0.001
      && Math.abs(f.offsetX) < 0.001
      && Math.abs(f.offsetY) < 0.001;
}

/* Magnitude constants for the subtle dynamics. Small numbers on
 * purpose — "subtle" means the viewer notices motion but isn't
 * pulled out of the content. */
export const DYNAMICS_ZOOM_RANGE = 0.10;  // scale ramps 1.0 → 1.10
export const DYNAMICS_PAN_RANGE = 0.10;   // offsetX shifts by 0.10 total (−0.05 → +0.05)

/** Apply optional dynamics (pan, zoom) on top of a static framing
 *  for a clip at progress p (0..1 from clip start to clip end).
 *  Returns the effective scale + offsets to plug into the existing
 *  containFit + offset math. */
export function applyDynamics(
  framing: ClipFraming,
  dynamics: { pan?: boolean; zoom?: boolean } | undefined,
  progress: number,
): ClipFraming {
  if (!dynamics || (!dynamics.pan && !dynamics.zoom)) return framing;
  const p = Math.max(0, Math.min(1, progress));
  let scale = framing.scale;
  let offsetX = framing.offsetX;
  if (dynamics.zoom) scale *= 1.0 + DYNAMICS_ZOOM_RANGE * p;
  if (dynamics.pan)  offsetX += -DYNAMICS_PAN_RANGE / 2 + DYNAMICS_PAN_RANGE * p;
  return { scale, offsetX, offsetY: framing.offsetY };
}
